import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import BugReportModal from 'src/pages/home/bug-report-modal';
import { ContactUserRole } from 'src/api/contact/types';
import { BugReportSource } from 'src/api/bug-report/types';

export const bugReportEvents = {
  _pulseListeners: new Set<() => void>(),
  _openModalListeners: new Set<(comment?: string, source?: BugReportSource, role?: ContactUserRole) => void>(),
  _iconVisibleListeners: new Set<(visible: boolean) => void>(),
  _iconVisible: false,

  pulse() {
    this._pulseListeners.forEach((fn) => fn());
  },
  onPulse(fn: () => void) {
    this._pulseListeners.add(fn);
    return () => this._pulseListeners.delete(fn);
  },
  /**
   * `role` solo lo lee el modal de respaldo del proveedor, el que sale cuando NO hay icono montado.
   * Sin el se abria siempre como `Anonymous`, y con ese rol el modal **pide nombre y correo que la
   * aplicacion ya sabe** y exige marcar la casilla de privacidad: justo lo que pasaba en pantalla
   * estrecha, donde la entrada es el menu de hamburguesa y el icono de la cabecera no existe.
   *
   * Lo que NO hace es viajar al servidor: `CreateBugReportCommandBody` solo lleva `Comment`,
   * `UserInfo` y `Source`, y el rol del reporte sale del token. Esto es del formulario y de nada mas
   * (j2l-website#1933).
   */
  openModal(comment?: string, source?: BugReportSource, role?: ContactUserRole) {
    this._openModalListeners.forEach((fn) => fn(comment, source, role));
  },
  onOpenModal(fn: (comment?: string, source?: BugReportSource, role?: ContactUserRole) => void) {
    this._openModalListeners.add(fn);
    return () => this._openModalListeners.delete(fn);
  },
  setIconVisible(visible: boolean) {
    this._iconVisible = visible;
    this._iconVisibleListeners.forEach((fn) => fn(visible));
  },
  onIconVisibleChange(fn: (visible: boolean) => void) {
    this._iconVisibleListeners.add(fn);
    return () => this._iconVisibleListeners.delete(fn);
  },
  iconVisible() {
    return this._iconVisible;
  }
};

interface BugReportContextValue {
  pulse: () => void;
  openModal: (comment?: string, source?: BugReportSource, role?: ContactUserRole) => void;
  iconVisible: boolean;
  pulseTick: number;
}

export const BugReportContext = createContext<BugReportContextValue>({
  pulse: () => {},
  openModal: () => {},
  iconVisible: false,
  pulseTick: 0
});

export const BugReportProvider: FC<PropsWithChildren> = ({ children }) => {
  const [iconVisible, setIconVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalComment, setModalComment] = useState<string | undefined>(undefined);
  const [modalSource, setModalSource] = useState<BugReportSource | undefined>(undefined);
  const [modalRole, setModalRole] = useState<ContactUserRole>(ContactUserRole.Anonymous);
  const [pulseTick, setPulseTick] = useState(0);

  useEffect(() => {
    const unsubPulse = bugReportEvents.onPulse(() => setPulseTick((t) => t + 1));
    const unsubModal = bugReportEvents.onOpenModal((comment, source, role) => {
      if (!bugReportEvents.iconVisible()) {
        setModalComment(comment);
        setModalSource(source);
        setModalRole(role ?? ContactUserRole.Anonymous);
        setModalOpen(true);
      }
    });
    const unsubIconVisible = bugReportEvents.onIconVisibleChange((visible) => {
      setIconVisible(visible);
    });
    return () => {
      unsubPulse();
      unsubModal();
      unsubIconVisible();
    };
  }, []);

  return (
    <BugReportContext.Provider
      value={{
        pulse: bugReportEvents.pulse.bind(bugReportEvents),
        openModal: bugReportEvents.openModal.bind(bugReportEvents),
        iconVisible,
        pulseTick
      }}
    >
      {children}
      <BugReportModal
        open={modalOpen}
        role={modalRole}
        initialComment={modalComment}
        initialSource={modalSource}
        onOk={() => {
          setModalOpen(false);
          setModalSource(undefined);
          setModalRole(ContactUserRole.Anonymous);
        }}
        onCancel={() => {
          setModalOpen(false);
          setModalSource(undefined);
          setModalRole(ContactUserRole.Anonymous);
        }}
      />
    </BugReportContext.Provider>
  );
};

export function useBugReport() {
  return useContext(BugReportContext);
}
